import React from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select, { components } from "react-select";

import { getElementByUuid } from "../../../utils/data/Content";
import ItemStatus from "../../../utils/ItemStatus";

import type { JSX } from "react";
import type {
  OptionProps,
  SingleValue,
  ValueContainerProps,
} from "react-select";

import type { SmartlistDataSourceComponent } from "../../../api/ProjectAPI.types";
import type { GetSmartlistResponse } from "../../../api/SmartlistAPI.types";
import type { AppState } from "../../../reducers";

export interface SmartlistOption {
  value: string;
  label: string;
}

type SmartlistOptionProps = OptionProps<SmartlistOption, false>;
type SmartlistValueContainerProps = ValueContainerProps<SmartlistOption, false>;

const SelectOption =
  components.Option as unknown as React.ComponentType<SmartlistOptionProps>;
const SelectValueContainer =
  components.ValueContainer as unknown as React.ComponentType<SmartlistValueContainerProps>;

interface SmartlistSelectProps {
  item: SmartlistDataSourceComponent;
  onChange: (option: SmartlistOption) => void;
}

export default function SmartlistSelect({
  item,
  onChange,
}: SmartlistSelectProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const { smartlists } = useSelector((state: AppState) => state.fetchReducer);

  const getSmartlistList = (): SmartlistOption[] => {
    const list: SmartlistOption[] = [];
    const smartlistTypes = ["standard", "series", "audio", "auto"];

    smartlists.data.forEach(
      (smartlistItem: { type: string; uuid: string; label: string }) => {
        const isValidType = smartlistTypes.includes(smartlistItem.type);

        if (isValidType) {
          list.push({
            value: smartlistItem.uuid,
            label: smartlistItem.label,
          });
        }
      },
    );

    return list;
  };

  const getSmartlistValue = (): SmartlistOption | null => {
    if (item.dynamic_component?.smartlist_uuid) {
      const valueUuid = item.dynamic_component.smartlist_uuid;
      const smartlistItem = getElementByUuid(valueUuid, smartlists);

      return {
        value: valueUuid,
        label: smartlistItem ? smartlistItem.label : "",
      };
    }
    return null;
  };

  const handleSmartlistSelection = (
    newValue: SingleValue<SmartlistOption>,
  ): void => {
    if (newValue) {
      onChange(newValue);
    }
  };

  const optionRenderer = ({
    children,
    ...props
  }: SmartlistOptionProps): JSX.Element => {
    const smartlistItem: GetSmartlistResponse = getElementByUuid(
      props.data.value,
      smartlists,
    );

    return (
      <div className="editor-select-custom-option">
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <SelectOption {...props}>
          <div className="editor-select-option-value">{children}</div>
          <div className="editor-select-option-indicator">
            <span
              className={`editor-item-status item-status-${ItemStatus.getSmartlistStatus(smartlistItem)}`}
            >
              {" "}
            </span>
          </div>
        </SelectOption>
      </div>
    );
  };

  const valueRenderer = ({
    children,
    ...props
  }: SmartlistValueContainerProps): JSX.Element => {
    const selectValue = props.getValue()[0];
    let smartlistItem = null;

    if (selectValue?.value) {
      smartlistItem = getElementByUuid(selectValue.value, smartlists);
    }

    return (
      <div className="editor-select-custom-option">
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <SelectValueContainer {...props}>
          <div className="editor-select-custom-option__container">
            <div className="editor-select-option-value">{children}</div>
            {smartlistItem ? (
              <div className="editor-select-option-indicator">
                <span
                  className={`editor-item-status item-status-${ItemStatus.getSmartlistStatus(smartlistItem)}`}
                >
                  {" "}
                </span>
              </div>
            ) : null}
          </div>
        </SelectValueContainer>
      </div>
    );
  };

  return (
    <div className="editor-form-row">
      <div className="editor-form-label">{t("selectSmartlist")}</div>
      <div className="editor-form-input" style={{ maxWidth: "400px" }}>
        <Select<SmartlistOption>
          closeMenuOnSelect
          className="editor-select"
          classNamePrefix="slct"
          name="form-field-name"
          onChange={handleSmartlistSelection}
          options={getSmartlistList()}
          placeholder={t("select")}
          value={getSmartlistValue()}
          components={{
            Option: optionRenderer,
            ValueContainer: valueRenderer,
          }}
        />
      </div>
    </div>
  );
}
